import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';

export enum LogType {
  interaction = 'INTE',
  performance = 'PERF'
}

export interface InteractionLog {
  name: string;
  event: string;
  type?: LogType;
  data?: any;
  timestamp: string;
}

export interface InteractionLogConfig {
  /** enable/disable printing log in the console */
  logToConsole?: boolean;
  /** enable/disable storing log in the session storage */
  logToSessionStorage?: boolean;
  /** enable/disable sending log to backend */
  logToServer?: boolean;
  /** enable/disable interaction logs */
  logInteractions?: boolean;
  /** enable/disable performance logs */
  logPerformance?: boolean;

  /** any other parameter */
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class InteractionLogService {
  private static $instance: InteractionLogService = null;

  /**
   * Configuration for the Log
   */
  configuration: InteractionLogConfig = undefined;

  username = '';
  endpoint = '';
  httpClient: HttpClient = undefined;

  /**
   * Observable for the logs list
   */
  get onLog(): Observable<Array<InteractionLog>> {
    return this.$onLog;
  }
  private logSubject = new Subject<Array<InteractionLog>>();
  private $onLog: Observable<Array<InteractionLog>> = this.logSubject.pipe();

  /**
   * Logs list
   */
  get logs(): Array<InteractionLog> {
    return this.$logs;
  }
  private $logs: Array<InteractionLog> = [];

  private window: Window;
  private queue: any[] = [];
  private sendingToServer: Promise<any> = undefined;

  constructor(@Inject(DOCUMENT) private document) {
    this.window = (this.document as Document).defaultView;
    InteractionLogService.$instance = this;
    (this.window as any).interactionLogService = this;
  }

  static instance(): InteractionLogService {
    return InteractionLogService.$instance;
  }

  private sendToServerInterval = 0;
  startSendToServerInterval() {
    if (!this.sendToServerInterval) {
      // log to server every five minutes
      this.sendToServerInterval = this.window.setInterval(() => this.logToServer(), 5 * 60 * 1000);
    }
  }

  performanceLog(name: string, event: string, data?: any) {
    this.log(name, event, LogType.performance, data);
  }

  interactionLog(name: string, event: string, data?: any) {
    this.log(name, event, LogType.interaction, data);
  }

  log(name: string, event: string, type: LogType, data?: any): void {
    if (!this.isLogEnabled(type)) return;

    event = event.toUpperCase();

    const log: InteractionLog = {
      name,
      event,
      type,
      timestamp: new Date().toJSON()
    };

    if (data !== undefined) {
      log.data = data;
    }

    this.$logs.push(log);

    this.configuration && this.configuration.logToConsole && console.log.call(console, ...this.consoleMessage(log));

    this.configuration &&
      this.configuration.logToSessionStorage &&
      this.window.sessionStorage.setItem('wfmInteractionLog', JSON.stringify(this.$logs));

    this.logSubject.next(this.$logs);
  }

  clearLogs() {
    this.$logs = [];
  }

  isLogEnabled(type: LogType): boolean {
    if (!this.configuration) return false;

    if (!this.configuration.logInteractions && type === LogType.interaction) return false;

    if (!this.configuration.logPerformance && type === LogType.performance) return false;

    return true;
  }

  private consoleMessage(log: InteractionLog): string[] {
    return [
      `%c[${log.type}]%c[${log.event}]%c[${log.timestamp}]%c[${log.name}]`,
      (log.event === 'ERROR' && 'color: red') || 'color: orange',
      'color: orange',
      'color: blue',
      'color: green',
      (log.data !== undefined && log.data) || ''
    ];
  }

  private logMessage(log: InteractionLog): string {
    return `[${log.type}][${log.event}][${log.timestamp}][${log.name}] ${
      (log.data !== undefined && JSON.stringify(log.data)) || ''
    }`;
  }

  private logToServer() {
    if (!this.configuration.logToServer) return;
    this.queue = [...this.queue, ...this.$logs];
    this.clearLogs();
    this.sendLogsToServer();
  }

  private async printLogToServer() {
    const logs = this.queue.splice(0, 100);
    const logsMessage: string[] = logs.map(log => this.logMessage(log));

    if (!logs || !logs.length) return;

    try {
      await this.httpClient
        .post(
          this.endpoint,
          {
            logMessageList: logsMessage,
            logType: ['INFO'],
            loginUser: this.username
          },
          {
            headers: new HttpHeaders({ 'Content-Type': 'application/json; charset=UTF-8' })
          }
        )
        .toPromise();
    } catch {
      this.queue.splice(0, 0, ...logs);
    }

    await this.printLogToServer();
  }

  async sendLogsToServer() {
    if (this.sendingToServer) {
      return await this.sendingToServer;
    }

    this.sendingToServer = this.printLogToServer();

    await this.sendingToServer;

    this.sendingToServer = undefined;
  }
}
